import React, { FC } from 'react';
import Icon from "../Icon/Icon";
import { P } from "../Text/Text";
import { DirectionE, IconColorE } from "../../models/enums";
import { clss } from '../../utils/helpers/styleHelpers';
import { useAppSelector } from '../../store/hooks';
import { selectDirection } from '../../store/selectors/appSelectors';

import styles from './pagination.module.scss';

type PaginationPropTypes = {
    prev: () => void;
    next: () => void;
    pages: null[];
    page: number;
    setPage: (value: number) => void;
    simplified?: boolean;
}

/**
 * Pagination component for any data
 */

const MAX_SHOWN_PAGES = 4;

const Pagination: FC<PaginationPropTypes> = ({ prev, next, pages, page: currentPage, setPage, simplified }) => {
    const direction = useAppSelector(selectDirection);
    const lastPage = pages?.length;
    const pagesList = pages?.map((_, index) => index + 1);

    const Item = ({ page }: any) => (
        <li
            key={page}
            className={clss([page === currentPage ? styles.active : '', page > 99 ? styles.expanded : ''])}
            onClick={() => setPage(page)}
        >
            {page}
        </li>
    );

    const SpaceItem = () => <li className={styles.spaceItem}>...</li>;

    const getVisiblePages = (currentPage: number, pageList: number[], lastPage: number) => {
        if (currentPage <= 3) {
            return pageList.slice(1, MAX_SHOWN_PAGES);
        } else if (currentPage >= (lastPage - 2)) {
            return pageList.slice((lastPage - MAX_SHOWN_PAGES) + 1, lastPage);
        } else {
            return pageList.slice(currentPage - 2, currentPage + 1);
        }
    };

    return pagesList?.length > 1 ? (
        <div className={clss([simplified ? styles.simplifiedWrapper: styles.wrapper])}>
            {!simplified && <P onClick={() => setPage(1)}>First</P>}
            <div className={styles.button} onClick={() => currentPage !== 1 && prev()}>
                <Icon
                    name={direction === DirectionE.LTR ? 'prev' : 'next'}
                    color={currentPage !== 1 ? IconColorE.PASTEL_BLUE : IconColorE.LIGHT_GRAY}
                />
            </div>
            <ul>
                <Item page={1} />
                {currentPage >= 3 && pages?.length > 4 && <SpaceItem />}
                {getVisiblePages(currentPage, pagesList, lastPage).map((page) => <Item page={page} key={page} />)}
                {currentPage <= (lastPage - 3) && pagesList?.length > MAX_SHOWN_PAGES && <SpaceItem />}
                {pagesList?.length > MAX_SHOWN_PAGES && (currentPage < (pages.length - 2)) && <Item page={lastPage} />}
            </ul>
            <div className={styles.button} onClick={() => currentPage !== pagesList.length && next()}>
                <Icon
                    name={direction === DirectionE.LTR ? 'next' : 'prev'}
                    color={currentPage !== pagesList.length ? IconColorE.PASTEL_BLUE : IconColorE.LIGHT_GRAY}
                />
            </div>
            {!simplified && <P onClick={() => setPage(pagesList.length)}>Last</P>}
        </div>
    ) : <div />;
};

export default Pagination;